import { useCallback, useEffect, useRef, useState } from "react";
import { MasterIndex, SensoterraMoistureSensor, ThirdPartyDeviceBase } from "verdi-web-client";
import { IrrigationDeviceBase } from "verdi-web-client/Models/Devices/IrrigationDeviceBase";
import { composeDeviceAttributes } from "../device-attributes/device-attributes";
import MapEntityBase from "../map-management/map-entities/map-entity-base";
import { POLLING_FREQUENCIES, PollingFrequency } from "./constants";
import { addPollingRule, recomputePollingInterval, removePollingRule } from "./device-polling-service";
import { createRule, RECENT_CONTACT_THRESHOLD } from "./polling-rules";
import { refreshChangedDevices } from "./refresh-devices";

/**
 * Returns a stable function reference whose implementation always reflects the latest closure.
 * Avoids registering a new polling rule on every render while still capturing current state.
 */
function useStableCallback<T>(fn: () => T): () => T {
    const ref = useRef(fn);
    ref.current = fn;
    return useCallback(() => ref.current(), []);
}

/**
 * Get the devices that are transitioning between manual and automatic modes
 */
const getTransitioningDevices = (): string[] => {
    const recentlyContactedDevices = [...MasterIndex.blockValve.all, ...MasterIndex.irrigationDevice.all].filter(
        (device) => {
            const deviceAttributes = composeDeviceAttributes({ model: device });
            const { isManualModeTransitioning } = deviceAttributes.operatingMode;
            const { lastContactDate } = deviceAttributes.network;

            return (
                isManualModeTransitioning &&
                lastContactDate &&
                Date.now() - new Date(lastContactDate?.valueOf() ?? 0).valueOf() < RECENT_CONTACT_THRESHOLD
            );
        },
    );

    return recentlyContactedDevices.map((device) => device.dbID);
};

/**
 * Hook to poll for devices that are transitioning between manual and automatic modes
 */
export const useManualModePolling = () => {
    useEffect(() => {
        // Reschedule the interval at the updated frequency immediately when we add this rule
        recomputePollingInterval();
    }, []);

    usePollingRule({
        frequency: POLLING_FREQUENCIES.MANUALMODE_TRANSITIONING,
        getDeviceIdsForPolling: getTransitioningDevices,
    });
};

/**
 * Hook to poll for devices that are focused (with an open device card)
 */
export const useDeviceCardPolling = ({
    isCardOpen,
    selectedMapEntity,
}: {
    isCardOpen: boolean;
    selectedMapEntity: MapEntityBase | null;
}) => {
    const deviceId = selectedMapEntity?.model?.dbID;

    useEffect(() => {
        // Reschedule the interval at the updated frequency immediately when card state changes.
        recomputePollingInterval();

        // Refresh device data immediately when we change cards (regardless of polling frequency)
        if (deviceId && isCardOpen) {
            refreshChangedDevices({
                includeSpecificDeviceIDs: [deviceId],
            });
        }
    }, [deviceId, isCardOpen]);

    const stableOnTick = useStableCallback<string[]>(() => (deviceId ? [deviceId] : []));

    usePollingRule({
        frequency: POLLING_FREQUENCIES.INFOCARD_OPEN,
        getDeviceIdsForPolling: stableOnTick,
    });
};

/**
 * Hook to poll for devices that are members of a zone
 */
export const useZoneDevicesPolling = (zoneId: string, shouldPoll: boolean) => {
    const [deviceIds, setDeviceIds] = useState<string[]>([]);

    useEffect(() => {
        const zone = MasterIndex.zone.byID[zoneId];
        const devices = zone?.devices ?? [];
        if (devices.length > 0 && shouldPoll) {
            setDeviceIds(
                devices
                    /**
                     * Ignore ThirdPartyDevice (NOTE: not the same as ThirdPartyDeviceBase) since they are raw schemas, not web-client models.
                     * This typing is potentially incorrect and not supported in verdi-web-client
                     */
                    .filter(
                        (device) =>
                            device instanceof ThirdPartyDeviceBase ||
                            device instanceof IrrigationDeviceBase ||
                            device instanceof SensoterraMoistureSensor,
                    )
                    .map((device) => device.id),
            );
        } else {
            setDeviceIds([]);
        }
    }, [zoneId, shouldPoll]);

    useEffect(() => {
        // Reschedule the interval at the updated frequency immediately when zone polling state changes.
        recomputePollingInterval();

        // Refresh device data immediately when zone polling state changes (regardless of polling frequency)
        if (deviceIds.length > 0 && shouldPoll) {
            refreshChangedDevices({
                includeSpecificDeviceIDs: deviceIds,
            });
        }
    }, [deviceIds, shouldPoll]);

    const stableOnTick = useStableCallback<string[]>(() => deviceIds);

    usePollingRule({
        frequency: POLLING_FREQUENCIES.ZONE_INFOCARD_OPEN,
        getDeviceIdsForPolling: stableOnTick,
    });
};

export interface UsePollingRuleParams {
    frequency: PollingFrequency;
    getDeviceIdsForPolling: () => string[];
}

/**
 * Hook to add (and clean up) a general polling rule.
 *
 * IMPORTANT: Both `getDeviceIdsForPolling` must be stable references across renders.
 * An unstable reference will cause the rule to be torn down and recreated on every render.
 */
export const usePollingRule = ({ frequency, getDeviceIdsForPolling }: UsePollingRuleParams) => {
    useEffect(() => {
        const rule = createRule(frequency, getDeviceIdsForPolling);
        addPollingRule(rule);
        return () => removePollingRule(rule);
    }, [frequency, getDeviceIdsForPolling]);
};
